import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { Gift, Copy, Check } from 'lucide-react';

export default function ReferralCard() {
  const { user } = useAuth();
  const [referralCode, setReferralCode] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadReferralInfo();
    }
  }, [user]);

  const loadReferralInfo = async () => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        let code = data.referralCode;

        // Generate referral code if doesn't exist
        if (!code) {
          code = generateReferralCode();
          await updateDoc(userRef, { referralCode: code });
        }

        setReferralCode(code);
        setReferralCount(data.referralCount || 0);
      }
    } catch (error) {
      console.error('Error loading referral info:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReferralCode = () => {
    // Generate a 6-character code from user's email
    const email = user.email || 'user';
    const hash = email.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `BEACH${hash.toString(36).toUpperCase().slice(0, 6)}`;
  };

  const getReferralLink = () => {
    return `${window.location.origin}?ref=${referralCode}`;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(getReferralLink());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-200 shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <Gift className="w-6 h-6 text-purple-600" />
        <h3 className="text-xl font-bold text-gray-800">Refer a Friend, Both Get 1 Month FREE!</h3>
      </div>

      <p className="text-gray-700 mb-4">
        Share your unique link. When someone signs up, you <strong>both</strong> get 1 month of Premium free! 🎉
      </p>

      {referralCount > 0 && (
        <div className="bg-white rounded-lg p-3 mb-4 border border-purple-200">
          <p className="text-sm text-gray-600">
            🏆 You've referred <strong className="text-purple-600">{referralCount}</strong> friend{referralCount !== 1 ? 's' : ''}!
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg p-4 border-2 border-purple-300">
        <label className="text-xs text-gray-600 font-semibold uppercase mb-2 block">Your Referral Link</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={getReferralLink()}
            readOnly
            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-lg bg-gray-50 text-sm font-mono"
          />
          <button
            onClick={copyToClipboard}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      <p className="text-xs text-gray-500 mt-3 text-center">
        Share via email, text, or social media. Unlimited referrals = unlimited free months!
      </p>
    </div>
  );
}
